import {MobxReactionUpdate} from '@adobe/lit-mobx';
import 'extensions/proforma/components/x-installation-item';
import {proformaStore} from 'extensions/proforma/data/proforma-store';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import styles from './x-installation-section.component.scss?as=stylesheet';

export class InstallationSection extends MobxReactionUpdate(LitElement) {
	static styles = [styles];

	static get properties () {
		return {
			code: {
				type: String
			},
			desktopPerRow: {
				attribute: 'desktop-per-row',
				type: Number
			},
			heading: {
				type: String
			},
			isHidden: {
				attribute: 'is-hidden',
				type: Number
			},
			mobilePerRow: {
				attribute: 'mobile-per-row',
				type: Number
			},
			multiSelect: {
				attribute: 'multi-select',
				type: Number
			},
			sectionOptions: {
				attribute: 'section-options',
				type: Object
			},
			sectionRequired: {
				attribute: 'section-required',
				type: Number
			},
			sectionType: {
				attribute: 'section-type',
				type: String
			},
			triggerCode: {
				attribute: 'trigger-code',
				type: String
			}
		};
	}

	constructor () {
		super();

		this.code = '';
		this.desktopPerRow = 1;
		this.heading = '';
		this.isHidden = 0;
		this.mobilePerRow = 1;
		this.multiSelect = 0;
		this.sectionOptions = {};
		this.sectionRequired = 0;
		this.sectionType = '';
		this.triggerCode = '';
	}

	determineVisibility () {
		if (!this.triggerCode) {
			return true;
		}

		const selected = [];

		for (const key of Object.keys(proformaStore?.state ?? {})) {
			for (const selection of proformaStore.state[key]) {
				if (!selection.code) {
					continue;
				}

				selected.push(selection.code);
			}
		}

		return selected.findIndex(element => element === this.triggerCode) > -1;
	}

	connectedCallback () {
		super.connectedCallback();

		// Check on intialization
		this.isHidden = !this.determineVisibility();

		window.addEventListener('option_click', () => {
			this.isHidden = !this.determineVisibility();
			this.requestUpdate();
		});
	}

	firstUpdated () {
		const options = {
			required: this.sectionRequired,
			section: this.code
		};

		this.dispatchEvent(new CustomEvent('section_render', {
			bubbles: true,
			composed: true,
			detail: options
		}));
	}

	disconnectedCallback () {
		window.removeEventListener('option_click');

		super.disconnectedCallback();
	}

	isSelected (code) {
		return this.state?.findIndex(x => x.code.toString() === code.toString()) > -1;
	}

	showHideSection () {
		return this.state?.findIndex(x => x.code.toString() === this.triggerCode.toString()) > -1;
	}

	renderInstallationItems () {
		return this.sectionOptions.map(option => {
			return html`
				<x-installation-item
					@change="${this._handleOptionSelection}"
					image="${option?.option_icon?.image}"
					color-swatch="${option?.option_color_swatch?.value}"
					label="${option?.option_label?.value}"
					additional-text="${option?.option_additional_text?.value}"
					tooltip-text="${option?.option_tooltip_text?.value}"
					code="${option?.option_code?.value}"
					section-code="${this.code}"
					section-type="${this.sectionType}"

					?has-terms="${option?.option_display_terms?.settings?.enabled}"
					terms="${option?.option_display_terms?.option_terms?.value}"

					product-code="${option?.option_product?.product?.code}"
					product-price="${option?.option_product?.product?.price}"

					?selected="${this.isSelected(option?.option_code?.value)}"
					multi-select="${this.multiSelect}"
				></x-installation-item>
			`;
		});
	}

	// Render the UI as a function of component state
	render () {
		const classes = {
			hidden: this.isHidden,
			'installation-section': true
		};

		const innerClasses = {
			'installation-options': this.sectionType === 'option_with_quantity',
			'installation-options-flexed': this.sectionType !== 'option_with_quantity',
			'installation-options-grid': true
		};

		innerClasses[`installation-options-desktop--${this.desktopPerRow}`] = true;
		innerClasses[`installation-options-mobile--${this.mobilePerRow}`] = true;

		return html`
			<div class="${classMap(classes)}">
				<h2 class="installation-section-heading">${this.heading}</h2>
				<div class="${classMap(innerClasses)}">
					${this.renderInstallationItems()}
				</div>
				${this.renderTerms()}
			</div>
		`;
	}

	renderTerms () {
		return this.sectionOptions.map(option => {
			return option?.option_display_terms?.settings?.enabled ? html`<x-installation-terms section="${option?.option_code?.value}" terms="${option?.option_display_terms?.option_terms?.value}"></x-installation-terms>` : nothing;
		});
	}

	get state () {
		return proformaStore?.state?.[this.code] ?? [];
	}
}

customElements.define('x-installation-section', InstallationSection);
